// OpenAI ChatGPT API service for chatbot integration using OpenAI SDK
import OpenAI from "openai";

// Get API key from environment variables for security
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

// Initialize OpenAI client only if API key is available
let openai = null;
if (OPENAI_API_KEY && OPENAI_API_KEY !== 'your-api-key-here') {
  openai = new OpenAI({
    apiKey: OPENAI_API_KEY,
    dangerouslyAllowBrowser: true // Required for client-side usage
  });
  console.log('✅ OpenAI client initialized with API key');
} else {
  console.log('⚠️ No valid OpenAI API key found - using fallback responses');
}

// Test function to verify OpenAI connection
export const testOpenAIConnection = async () => {
  try {
    console.log('🔍 Testing OpenAI connection...');
    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      messages: [{ role: "user", content: "Say hello" }],
      max_tokens: 50
    });
    console.log('✅ OpenAI connection test successful:', response);
    return response.choices[0].message.content;
  } catch (error) {
    console.error('❌ OpenAI connection test failed:', error);
    return null;
  }
};

export const sendMessageToChatGPT = async (message, conversationHistory = []) => {
  // If no OpenAI client is available, use fallback responses
  if (!openai) {
    console.log('⚠️ No OpenAI client available - using fallback responses');
    return await getFallbackResponse(message);
  }

  try {
    console.log('🤖 Attempting OpenAI API call...', { message, historyLength: conversationHistory.length });
    
    // Format conversation history for OpenAI API
    const messages = [
      {
        role: "system",
        content: `You are ChatGPT, a helpful and knowledgeable AI assistant created by OpenAI. You can help with a wide variety of topics including:
        - General questions and knowledge
        - Programming and technology
        - Writing and communication
        - Analysis and problem-solving
        - Creative tasks
        - Educational topics
        - Work and productivity advice
        - And much more!
        
        While the user is currently using a Student Management System, feel free to answer any questions they might have about any topic. Be helpful, accurate, and conversational. If you're unsure about something, be honest about the limitations of your knowledge.`
      },
      ...conversationHistory.slice(-8).map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      })),
      {
        role: "user",
        content: message
      }
    ];

    console.log('📝 Formatted messages:', messages);

    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini", // Using the model you specified
      messages: messages,
      max_tokens: 300,
      temperature: 0.7,
      top_p: 1,
      frequency_penalty: 0,
      presence_penalty: 0
    });

    console.log('✅ OpenAI API response received:', response);
    
    const content = response.choices[0].message.content.trim();
    console.log('📤 Returning content:', content);
    
    return content;

  } catch (error) {
    console.error('❌ OpenAI API error - Full error object:', error);
    console.error('❌ Error message:', error.message);
    console.error('❌ Error stack:', error.stack);
    
    // Check for specific error types
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.error('🚫 CORS or Network Error detected');
    }
    
    if (error.status) {
      console.error('❌ HTTP Status:', error.status);
    }
    
    if (error.code) {
      console.error('❌ Error Code:', error.code);
    }
    
    console.log('🔄 Falling back to generic responses...');
    return await getFallbackResponse(message);
  }
};

// Fallback responses when OpenAI API is not available
const getFallbackResponse = async (message) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
  
  const lowerMessage = message.toLowerCase();
  
  // Programming and Technology
  if (lowerMessage.includes('code') || lowerMessage.includes('program') || lowerMessage.includes('javascript') || lowerMessage.includes('react') || lowerMessage.includes('python')) {
    const techResponses = [
      "I'd be happy to help with programming questions! What specific coding challenge or concept would you like to discuss?",
      "Programming can be complex, but I'm here to help break it down. What language or framework are you working with?",
      "That's a great technical question! I can help you with coding concepts, debugging, and best practices. What specifically do you need help with?",
      "I love helping with programming! Whether it's JavaScript, Python, React, or any other technology, I'm here to assist. What's your question?"
    ];
    return techResponses[Math.floor(Math.random() * techResponses.length)];
  }
  
  // General knowledge and education
  if (lowerMessage.includes('explain') || lowerMessage.includes('what is') || lowerMessage.includes('how does') || lowerMessage.includes('why')) {
    const explanationResponses = [
      "That's an interesting question! I'd be happy to explain that concept. Could you provide a bit more context about what specific aspect you'd like to understand?",
      "Great question! I can help explain that. What level of detail would be most helpful for you?",
      "I love explaining things! Let me break that down for you, though I might need a bit more context to give you the most relevant answer.",
      "Excellent question! I can provide explanations on a wide range of topics. What specifically would you like to learn about?"
    ];
    return explanationResponses[Math.floor(Math.random() * explanationResponses.length)];
  }
  
  // Math and calculations
  if (lowerMessage.includes('calculate') || lowerMessage.includes('math') || lowerMessage.includes('equation') || /\d+[\+\-\*\/]\d+/.test(lowerMessage)) {
    return "I can help with math problems and calculations! Feel free to share the specific problem you're working on, and I'll walk through it step by step with you.";
  }
  
  // Writing and communication
  if (lowerMessage.includes('write') || lowerMessage.includes('email') || lowerMessage.includes('letter') || lowerMessage.includes('essay')) {
    return "I'm great at helping with writing tasks! Whether it's drafting emails, essays, creative writing, or other documents, I can help with structure, tone, and content. What are you looking to write?";
  }
  
  // Creative tasks
  if (lowerMessage.includes('creative') || lowerMessage.includes('idea') || lowerMessage.includes('brainstorm') || lowerMessage.includes('story')) {
    return "I love creative projects! I can help with brainstorming ideas, creative writing, problem-solving, or any other creative challenges you're working on. What's your project about?";
  }
  
  // Science and knowledge
  if (lowerMessage.includes('science') || lowerMessage.includes('physics') || lowerMessage.includes('chemistry') || lowerMessage.includes('biology')) {
    return "Science is fascinating! I can help explain scientific concepts, answer questions about different fields, or discuss recent developments. What scientific topic interests you?";
  }
  
  // Business and productivity
  if (lowerMessage.includes('business') || lowerMessage.includes('productivity') || lowerMessage.includes('work') || lowerMessage.includes('career')) {
    return "I can help with business advice, productivity tips, career guidance, and workplace strategies. What specific area would you like to discuss?";
  }
  
  // Student Management System specific (still available but not dominant)
  if (lowerMessage.includes('growth session') || lowerMessage.includes('rating') || lowerMessage.includes('pip') || lowerMessage.includes('evaluation')) {
    return "I can help with Student Management System questions too! For platform-specific features like growth sessions or ratings, I can provide guidance, though you might also want to check the system documentation for detailed steps.";
  }
  
  // Greetings
  if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
    const greetings = [
      "Hello! I'm ChatGPT, your AI assistant. I can help with a wide range of topics - from programming and technology to general questions, writing, math, and creative projects. What would you like to explore today?",
      "Hi there! I'm here to help with whatever you need - whether it's work-related questions, learning something new, solving problems, or just having a conversation. How can I assist you?",
      "Hey! I'm ChatGPT, and I'm happy to help with all sorts of questions and tasks. What's on your mind today?",
      "Hello! I'm your AI assistant, ready to help with anything from technical questions to creative projects, learning, and problem-solving. What can I help you with?"
    ];
    return greetings[Math.floor(Math.random() * greetings.length)];
  }
  
  // Thank you responses
  if (lowerMessage.includes('thank')) {
    const thankResponses = [
      "You're very welcome! I'm always happy to help. Feel free to ask if you have any other questions about anything at all!",
      "Glad I could help! Is there anything else you'd like to know or discuss?",
      "Happy to assist! I'm here whenever you need help with any topic.",
      "You're welcome! I enjoy helping with all kinds of questions and problems. What else can I help you with?"
    ];
    return thankResponses[Math.floor(Math.random() * thankResponses.length)];
  }
  
  // Weather and current events
  if (lowerMessage.includes('weather') || lowerMessage.includes('news') || lowerMessage.includes('current')) {
    return "I don't have access to real-time information like current weather or news, but I can help you understand weather patterns, discuss historical events, or explain how to find current information from reliable sources!";
  }
  
  // Generic helpful responses
  const genericResponses = [
    "I'm here to help with any questions you might have! Whether it's about technology, general knowledge, writing, problem-solving, or anything else - just let me know what you're curious about.",
    "That's an interesting topic! I'd be happy to help you explore that further. Could you tell me a bit more about what specifically you'd like to know?",
    "I can assist with a wide variety of topics and questions. What would you like to discuss or learn about today?",
    "Feel free to ask me about anything - I'm here to help whether it's work-related, educational, creative, or just general curiosity!",
    "I'm designed to be helpful across many different areas. What kind of assistance are you looking for today?",
    "That sounds like something I can help with! Could you provide a bit more detail about what you're looking for?"
  ];
  
  return genericResponses[Math.floor(Math.random() * genericResponses.length)];
};

// Helper function to get API key setup instructions
export const getApiKeyInstructions = () => {
  return `To enable full ChatGPT capabilities:

1. Get an API key from OpenAI:
   - Visit: https://platform.openai.com/api-keys
   - Sign up for an account (new users get free credits)
   - Create a new API key
   - Copy the key (starts with 'sk-')

2. Add the API key to your environment:
   - Create a .env file in your project root
   - Add: REACT_APP_OPENAI_API_KEY=your_api_key_here
   - Restart your development server

3. Usage and Costs:
   - GPT-3.5-turbo is very cost-effective
   - New users typically get $5 in free credits
   - Cost is approximately $0.002 per 1K tokens

For now, I'm using built-in responses that work great for most questions!`;
};

// Export the main function with the original name for compatibility
export { sendMessageToChatGPT as sendMessageToClaude };